import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  DocumentationAdminTopicsDocument,
  useDocumentationAdminCreateTopicMutation,
  useDocumentationAdminUpdateTopicMutation,
} from "../../../generated/graphql";
import { throwOnGraphqlErrors } from "../../api-keys-admin/throw-on-graphql-errors";

const styles = {
  content: { pt: 1 } satisfies SxProps<Theme>,
};

export type TopicDialogTopic = {
  readonly uuid: string;
  readonly title: string;
  readonly description?: string | null;
};

type TopicDialogProps = {
  readonly open: boolean;
  /** The topic being edited, or `null` to create one. */
  readonly topic: TopicDialogTopic | null;
  readonly onClose: () => void;
  readonly onSaved: (topicUuid: string) => void;
};

const TopicDialog = ({ open, topic, onClose, onSaved }: TopicDialogProps) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle(topic?.title ?? "");
      setDescription(topic?.description ?? "");
      setError(null);
    }
  }, [open, topic]);

  const refetchQueries = [DocumentationAdminTopicsDocument];
  const [createTopic, { loading: creating }] =
    useDocumentationAdminCreateTopicMutation({ refetchQueries });
  const [updateTopic, { loading: updating }] =
    useDocumentationAdminUpdateTopicMutation({ refetchQueries });

  const handleSave = async () => {
    setError(null);
    const trimmedDescription = description.trim();
    try {
      if (topic === null) {
        const result = await createTopic({
          variables: {
            input: {
              title: title.trim(),
              description:
                trimmedDescription === "" ? null : trimmedDescription,
            },
          },
        });
        throwOnGraphqlErrors(result);
        const uuid = result.data?.documentationAdminCreateTopic.uuid;
        if (uuid !== undefined) {
          onSaved(uuid);
        }
        return;
      }
      const updated = await updateTopic({
        variables: {
          input: {
            uuid: topic.uuid,
            title: title.trim(),
            description: trimmedDescription === "" ? null : trimmedDescription,
          },
        },
      });
      throwOnGraphqlErrors(updated);
      onSaved(topic.uuid);
    } catch (saveError) {
      setError(
        saveError instanceof Error ? saveError.message : String(saveError),
      );
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>{topic === null ? "New topic" : "Edit topic"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={styles.content}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          <TextField
            autoFocus
            required
            label="Title"
            value={title}
            inputProps={{ maxLength: 200 }}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
          <TextField
            multiline
            minRows={2}
            label="Description"
            helperText="Shown under the topic name on the customer Documentation tab."
            value={description}
            inputProps={{ maxLength: 1000 }}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={title.trim() === "" || creating || updating}
          onClick={() => {
            void handleSave();
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TopicDialog;
